/**
 * API communication layer.
 * Orchestrates the snapshot → fetch → animate cycle for all server interactions.
 * Callbacks are injected by boot.js#initApiCallbacks to avoid circular dependencies — the game
 * graph they reach into is lazy (game-graph.js), so this module must never import it back.
 */
import { API } from "./constants.js";
import {
  getApp,
  isAnimating,
  setAnimating,
  setHadApiError,
  isIntentionalClear,
  setIntentionalClear,
  isDispatchTailBusy,
  whenDispatchTailIdle,
  isChainHalted,
  failPromptCommit,
  finishPromptCommit,
} from "./state.js";
import { fadeNodeOut } from "./animate.js";
import { decodeWireState } from "./serde/decode-wire-state.js";
import {
  readGameHandle,
  persistGameHandle,
  removeGameHandle,
} from "./game-handle.js";
import {
  ensureOpen as ensurePushStreamOpen,
  close as closePushStream,
  claimSeq,
  noteApplied,
  rebaseWatermark,
  isBufferEmpty,
} from "./push-stream.js";
import { noteServerState, resetWaitingState } from "./waiting-state.js";

/** CSRF protection — required on all POST requests. See CsrfFilter.java. */
const CSRF_HEADERS = { "X-Requested-With": "XMLHttpRequest" };

/**
 * The explicit game handle (multiplayer-addressable-games design § Client): a module-level id set
 * from create/restore/scenario-load responses, persisted so a reload recovers it without a fresh
 * game-start. api.js owns this cache-plus-guard layer — the single API seam, same reasoning as
 * CSRF_HEADERS above — over game-handle.js's raw storage primitives (multiplayer-surface design §
 * Structure: the key constant and its bare get/set/remove moved there once multiplayer-page.js
 * became a second concrete reader; this cache and the sibling-tab guard below stay here, unchanged
 * in semantics and signatures — round-2 finding).
 */
// localStorage is browser/jsdom-only. A test file can reach this module transitively (e.g.
// board-layout.spatial-phase.test.js → board-layout.js → game-menu.js → render.js → api.js)
// without ever calling one of its functions and without the `@vitest-environment jsdom` pragma
// (the default vitest environment is plain Node, no global localStorage) — the module import
// itself must not crash on that path, so the read is guarded rather than assumed.
let currentGameId =
  typeof localStorage !== "undefined" ? readGameHandle() : null;

/** Sets (and persists) the current game handle — called with a create/restore/scenario gameId. */
export function setCurrentGame(id) {
  currentGameId = id;
  persistGameHandle(id);
}

/** The current game handle, or null if none is set — read by callers that don't hold their own. */
export function getCurrentGame() {
  return currentGameId;
}

/**
 * Exported for tests only: `currentGameId` is set once from localStorage at module load and no
 * production path clears it back to "unset" outright (a 404, deleteGame, and showGameStart's quit
 * all clear it, but each as a side effect of a real transition) — a test file's beforeEach needs a
 * direct reset so the
 * "no stored handle" precondition holds regardless of test order within the file (localStorage.clear()
 * alone doesn't touch this module-level variable once it's been set by an earlier test).
 */
export function clearCurrentGame() {
  const id = currentGameId;
  currentGameId = null;
  // Only remove the persisted handle when it still names the game being cleared — a sibling tab
  // may have started a newer game since (localStorage is shared, the module var is per-tab), and
  // this tab's quit/404 must not wipe that game's only client handle.
  if (id == null || readGameHandle() === id) {
    removeGameHandle();
  }
}

/** @type {Function|null} Called before API fetch — clears hint timer + captures snapshot. */
let onBeforeApi = null;

/** @type {Function|null} Called after API fetch — renderWithAnimation(state, endpoint, params, snapshot). */
let onAfterApi = null;

/** @type {Function|null} Called by fetchState — render(state). */
let onFetchRender = null;

/**
 * @type {Function|null} Called when the chain rests at the human's card play —
 * boot.js wires card-queue.js#maybeDrainCardQueue. Injected like the callbacks
 * above: card-queue.js imports api() for its submits, so a direct import here
 * would be a module cycle.
 */
let onCardPlayRest = null;

/** Wire the chain-rest drain callback — called from boot.js#init. */
export function initChainRest(cb) {
  onCardPlayRest = cb;
}

/** Prompt commits come in via /api/submit with a move type that pairs with a prompt bar. */
const PROMPT_MOVE_TYPES = new Set([
  "bid",
  "hand-decision",
  "declare",
  "play-ouvert",
  "discard",
]);

function isPromptCommit(endpoint, params) {
  return endpoint === "submit" && PROMPT_MOVE_TYPES.has(params?.type);
}

/**
 * Wire up the render/snapshot callbacks — called from boot.js#initApiCallbacks.
 * @param {Function} beforeCb - (endpoint, params) => snapshot
 * @param {Function} afterCb  - (state, endpoint, params, snapshot) => Promise
 * @param {Function} renderCb - (state) => void
 */
export function initApi(beforeCb, afterCb, renderCb) {
  onBeforeApi = beforeCb;
  onAfterApi = afterCb;
  onFetchRender = renderCb;
}

/**
 * Single transport for `POST /api/{endpoint}`. All POSTs must route through this so the
 * CSRF header lives in one place — direct `fetch()` to `/api/**` is rejected by
 * `CsrfFilter`. Returns the raw `Response` so callers decide how to parse and recover.
 */
export function postApi(endpoint, params = {}) {
  return fetch(`${API}/${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...CSRF_HEADERS },
    body: JSON.stringify(params),
  });
}

/**
 * GET a JSON endpoint and return the parsed body. No CSRF header — CsrfFilter
 * only guards POST; GETs pass through (see CsrfFilter.java).
 */
export async function getApi(endpoint) {
  const res = await fetch(`${API}/${endpoint}`);
  if (!res.ok) throw new Error(`GET /api/${endpoint} → ${res.status}`);
  return res.json();
}

function requireCurrentGame(caller) {
  if (currentGameId == null) {
    throw new Error(
      `${caller}: no current game — setCurrentGame was never called`,
    );
  }
  return currentGameId;
}

/**
 * `POST /api/games/{id}/{endpoint}` — the game-scoped sibling of postApi. Throws if no game
 * handle is set (a caller bug, not routine input — every game-scoped call site runs only once a
 * game exists). Returns the raw Response, same contract as postApi.
 */
export function postGame(endpoint, params = {}) {
  const id = requireCurrentGame(`postGame(${endpoint})`);
  return fetch(`${API}/games/${id}/${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...CSRF_HEADERS },
    body: JSON.stringify(params),
  });
}

/** `GET /api/games/{id}/{endpoint}` — the game-scoped sibling of getApi. */
export async function getGame(endpoint) {
  const id = requireCurrentGame(`getGame(${endpoint})`);
  const res = await fetch(`${API}/games/${id}/${endpoint}`);
  if (!res.ok)
    throw new Error(`GET /api/games/${id}/${endpoint} → ${res.status}`);
  return res.json();
}

/**
 * `DELETE /api/games/{id}` — abandons the current game (the retired /api/clear-session's
 * replacement). Clears the local handle immediately (optimistic — the caller is navigating away
 * regardless) and fires the request without awaiting its outcome, the same fire-and-forget shape
 * postApi("clear-session") had. A no-op promise when no handle is set (nothing to delete), never a
 * throw — unlike postGame/getGame this is called from a teardown path that must not itself fail.
 *
 * Not the quit path (no-quit-cheesing): quitting must never abandon the in-flight round, so
 * showGameStart no longer calls this — it clears the local handle only (clearCurrentGame) and
 * leaves the game live server-side, resumable via Play → restore. This function and the endpoint
 * stay for their one remaining caller, dev/test-scenarios.js's real teardown between scenarios.
 */
export function deleteGame() {
  const id = currentGameId;
  clearCurrentGame();
  if (id == null) return Promise.resolve();
  return fetch(`${API}/games/${id}`, { method: "DELETE" });
}

/**
 * Client-side mirror of the server's no-game sparse DTO (GameStateDto.noSession()) for paths with
 * no game to address at all — boot with no stored handle, a stale-handle 404, or a just-deleted
 * game. decodeWireState only ever reads fields defensively (`?? ...`), so decoding `{}` yields the
 * same effective shape render() needs: it branches only on `state.phase`, absent here exactly as
 * it is in the real no-session DTO.
 */
function noGameState() {
  return decodeWireState({});
}

/**
 * Logical "action" name for animation/hint routing. For the unified /api/submit
 * endpoint, the action is the move type (bid, play-card, ...). Other endpoints
 * keep their name.
 */
function actionOf(endpoint, params) {
  return endpoint === "submit" && params?.type ? params.type : endpoint;
}

/**
 * The shared application funnel (sse-transport-swap design § Frontend): every state this client
 * ever applies — a POST response or a pushed stream message — routes through here. Decode is the
 * caller's; this owns everything from the seq gate onward. A stale response (`state.seq` doesn't
 * advance the ingest's watermark — e.g. it lost a race against a resync) still completes its
 * request-local bookkeeping (the prompt-commit lock, normally cleared by drainBurst's
 * setAnimating(false) inside the render this call skips) but not state application — the action
 * still succeeded server-side, only rendering it would be stale.
 *
 * Gates on `claimSeq`, not a plain seq-exceeds-watermark check (multiplayer per-seat SSE design §
 * (b)): since action transitions started reaching the stream too, a response and its own stream
 * duplicate can both arrive here for the same `seq` — `claimSeq` is the synchronous reservation
 * that lets exactly one of the two apply, the other taking this same stale-seq bookkeeping-only
 * path below.
 */
export async function applyServerState(state, action, params, snapshot) {
  if (!claimSeq(state.seq)) {
    finishPromptCommit();
    return;
  }
  // The waiting-state machine's ingest hook (multiplayer waiting-state UX design § The stage
  // machine) — after the claim succeeds (a claim-losing stale duplicate never reaches past the
  // guard above, so it never feeds the machine a state this tab didn't apply), but BEFORE
  // onAfterApi's mutation dispatch, mirroring renderFetchedState's own ingest-before-render order
  // below. A mutation's own resting renderPromptZone call (e.g. commitBiddingRestingState) must
  // see this machine's clocks already reflecting `state`, not the prior wait — otherwise
  // `waitingLineVisible()`'s impure read differs between that call and this function's own
  // not-applicable rerender, and promptContentKey sees two different keys for the identical final
  // content: a spurious full rebuild that flickers and orphans any tween the first (stale-keyed)
  // build started (e.g. the bid-value digit-rise, gated on lastBidKey in appEl.dataset).
  noteServerState(state);
  if (onAfterApi) {
    await onAfterApi(state, action, params, snapshot);
  }
  noteApplied(state.seq);
  if (state.phase) ensurePushStreamOpen(currentGameId);
  // Card-queue drain at the settled turn arrival (queue spec § Timing): the state rests — if it
  // rests at the human's card play with nothing still buffered ahead of it (the walk isn't mid-
  // landing), a queued head can fire as an ordinary submit. A halted chain never drains: it means
  // the board froze for reload — firing queued submits into it would race recovery.
  if (
    isBufferEmpty() &&
    state.phase === "PLAYING" &&
    state.pendingAction === "AWAIT_CARD_PLAY" &&
    !isChainHalted()
  ) {
    onCardPlayRest?.();
  }
}

/**
 * POST to an API endpoint with the full snapshot → fetch → animate cycle.
 * Blocks if an animation is already in progress.
 * Returns whether the POST was accepted — false on the animation-lock drop and
 * on HTTP failure. A 2xx returns true whether or not the funnel actually applied
 * the response, so this is an acceptance signal, not a completion one (card-queue
 * reset rule 6 reads it).
 */
export async function api(endpoint, params = {}) {
  if (isAnimating()) return false;

  const action = actionOf(endpoint, params);

  // Snapshot positions BEFORE the API call (elements still in old state)
  const snapshot = onBeforeApi ? onBeforeApi(action, params) : null;

  const res = await postGame(endpoint, params);
  if (!res.ok) {
    console.error(`API ${endpoint} failed: ${res.status}`);
    recoverFromApiError(endpoint, params);
    return false;
  }
  const state = decodeWireState(await res.json());
  await applyServerState(state, action, params, snapshot);
  return true;
}

/**
 * Pipelined bid submit — the bid pipeline's transport (bidding/bid-pipeline.js). Deliberate
 * differences from api(): no isAnimating() guard (the pipeline replaces it — clicks mid-replay
 * are the point), no onBeforeApi (bid actions capture no hand snapshot; the pipeline owns its
 * own clearHintTimer), and the funnel is fired WITHOUT awaiting it — its promise resolves at
 * burst completion, and awaiting it would pace clicks at replay speed. Returns the parsed state
 * (the pipeline serializes on response arrival) plus the un-awaited funnel promise (`settled`) so
 * the pipeline can attach its rejection backstop — a backstop here would need an api → pipeline
 * import cycle to reset the pipeline. The server walks any resulting display pause itself now, so
 * there is no chain to advance afterward — `settled` just reports its applied seq like every
 * response. Transport error → recoverFromApiError (as everywhere) + throw, so the pipeline resets
 * itself.
 */
export async function submitBidPipelined(params) {
  const res = await postGame("submit", params);
  if (!res.ok) {
    console.error(`API submit (pipelined bid) failed: ${res.status}`);
    recoverFromApiError("submit", params);
    throw new Error(`bid submit failed: ${res.status}`);
  }
  const state = decodeWireState(await res.json());
  const settled = applyServerState(state, "bid", params, null);
  return { state, settled };
}

/**
 * The network half of the boot state read: GET /api/games/{id}/state, decoded, no render. Split
 * from the render half (renderFetchedState) so boot.js#init can start the request on the first
 * module tick — parallel to the i18n fetch and the font gate — and hold the render until messages
 * resolve (the first render calls t()). fetchState below recomposes the two for the recovery
 * callers.
 *
 * No stored handle at all → no network call, straight to the no-game state (boot.js#init's first
 * load, and the tail of showGameStart's local-only clear). A 404 (the handle names a game that's gone —
 * evicted-then-expired-elsewhere, or another device deleted it) clears the handle and falls back
 * the same way, per the design's "404 → clear it, land on game-start" contract.
 */
export async function beginStateFetch() {
  if (currentGameId == null) return noGameState();
  const res = await fetch(`${API}/games/${currentGameId}/state`);
  if (res.status === 404) {
    clearCurrentGame();
    return noGameState();
  }
  if (!res.ok) {
    throw new Error(`GET /api/games/${currentGameId}/state → ${res.status}`);
  }
  return decodeWireState(await res.json());
}

/**
 * The render half of the boot state read: tail guard, then render. Returns the state, or
 * undefined when the render is skipped into an occupied dispatch tail (nothing settled to act on).
 *
 * Kept separate from beginStateFetch so the guard still evaluates at RENDER time, not fetch entry:
 * a render here while the tail is occupied tears down the persistent nodes that work owns (the
 * instant-then-animate clobber). recoverFromApiError fires the fetch asynchronously, so a burst can
 * start between the GET and now. Skip — not defer — the tail rests at the authoritative state; a
 * deferred render would re-apply stale data.
 *
 * The guard is phase-blind, and stays so. Skipping is not deferring, so a TEARDOWN render dropped
 * here would strand the board showGameStart has already faded to `autoAlpha: 0` — but the answer
 * is on the caller's side, and it RETRIES rather than betting on one attempt: showGameStart waits
 * for an idle tail after its fade, fetch-renders, then re-waits and re-renders if anything claimed
 * the tail in between. A teardown render this guard drops is therefore re-fired, not lost.
 * Deferring a teardown is safe where deferring a resync is not — "no game" cannot go stale while
 * it waits.
 * Exempting a phase-less state HERE was tried and reverted, and must not come back: it does not
 * stop the tail, so the burst keeps draining and `advanceCurrentState` re-seats a phase-ful state
 * under the start screen, which the next resize rebuilds the game board from (the failure
 * game.js#render's own `setCurrentState` comment records). api.tail-guard.test.js and
 * boot.test.js pin the phase-blindness at both layers.
 *
 * The guard covers everything chained on the tail, not just a burst mid-mutation
 * (state.js#isDispatchTailBusy): a gap-timeout resync used to land in the window where a bid
 * click's burst was QUEUED behind the round-start replay, render the server's current state, and
 * then be overwritten by that burst's older one — a client wedged at a state the server had left
 * (resync-render-lands-mid-bid-replay). Skipping costs nothing: the rebaseWatermark below is
 * skipped with it, so the messages buffered behind the unapplied seq survive and apply once the
 * tail drains (push-stream.js#noteApplied re-arms on every apply).
 *
 * Also the push-stream lifecycle authority (sse-transport-swap design): opens the stream for a
 * live phase, closes it otherwise — after the render, never before, so no stream message can
 * arrive before the board it would apply against exists. Every caller funnels through here
 * (boot's first render, recovery's fetchState, a quit's final fetchState), so no separate
 * open/close call is needed at those call sites. Also the ingest's designated resync authority:
 * the ONE deliberate unconditional seq re-base (covers the lost-response edge and the
 * session-expired edge, where a fresh HTTP session restarts the counter below the client's
 * watermark) — every other path only ever advances the watermark forward.
 *
 * One carve-out: a multiplayer board-boot deal (multiplayer-surface design § Entering a run —
 * the board deals; `boot.js#dealBoardBootRound`) mutates server state — and opens the stream
 * itself, via `applyServerState`'s own open call — from inside `onFetchRender`, without this
 * function's own `state` parameter (captured before the render) ever reflecting the new phase.
 * Left unguarded, this stale-`state` read below would immediately re-close what the deal just
 * opened (review finding — verified by trace, not just read). `state.multiplayerRun` on the
 * pre-render snapshot is unique to exactly that path — a solo pristine boot never sets it, and a
 * warm/live render's `state` already carries its own real phase — so gating the close on its
 * absence leaves every other caller's contract untouched.
 *
 * Async since lazy-game-graph-boot-seam: onFetchRender (boot.js's router) may itself await the
 * lazy game graph (a cold reload landing on a live game) — awaiting it here lets boot.js#init hold
 * the boot overlay through that wait instead of dissolving into a still-loading board.
 */
export async function renderFetchedState(state) {
  if (isDispatchTailBusy()) {
    console.warn(
      "[recovery] skipped render into an occupied dispatch tail — the tail is authoritative",
    );
    return;
  }
  rebaseWatermark(state?.seq ?? 0);
  // The waiting-state machine's second ingest funnel — the fetch/resync path isn't covered by
  // applyServerState (no response/stream message reaches it, only a GET's own decoded body).
  // isResync mirrors this function's own rebaseWatermark call above: the designated recovery
  // authority may re-anchor waiting-state's clock unconditionally too, even backward (a same-game
  // server restart resets GameStreamRegistry's in-memory seq counter below an off-turn wait's
  // last-seen value — waiting-state.js#continueWait's own doc has the full reasoning).
  noteServerState(state, { isResync: true });
  if (onFetchRender) {
    await onFetchRender(state);
  }
  if (state?.phase) {
    ensurePushStreamOpen(currentGameId);
  } else if (!state?.multiplayerRun) {
    closePushStream();
  }
  return state;
}

/**
 * GET initial state on page load and render without animation. Returns the fetched state so the
 * load path (boot.js#init) can resume a parked display pause; returns undefined when the render is
 * skipped into a live burst (nothing settled to act on). Recovery callers (recoverFromApiError,
 * showGameStart) use this composed form; init() drives the two halves directly to parallelize the
 * state GET against the i18n fetch on boot.
 */
export async function fetchState() {
  return renderFetchedState(await beginStateFetch());
}

/**
 * Recover from an API error (e.g. lost session after deploy, or race condition).
 * Clears animation lock and re-fetches current state from the server.
 * If the session was lost, the server returns a no-game state → game-start screen.
 * If the session is still valid (transient error), the game re-renders and continues.
 * Does NOT clear the session — avoids wiping a valid game on a transient 400.
 */
function recoverFromApiError(endpoint, params) {
  if (isIntentionalClear()) return;
  setHadApiError(true);
  if (isPromptCommit(endpoint, params)) {
    failPromptCommit();
  } else {
    finishPromptCommit();
  }
  setAnimating(false);
  // Fire-and-forget by design; if the recovery fetch itself fails there is nothing further to
  // drive — log instead of leaking an unhandled rejection (beginStateFetch throws on non-ok
  // since the gameId handle; the old /api/state fetch never did).
  fetchState().catch((e) =>
    console.error("[recovery] state re-fetch failed:", e.message),
  );
}

/**
 * Return to the start screen — client-side only (no-quit-cheesing): quitting never abandons the
 * in-flight round. The game stays live server-side; only the local handle is cleared, so Play →
 * restore picks the same game back up at the exact position ("the game remembers you"). Before
 * this, quit fired `deleteGame()` — the DELETE endpoint itself and `deleteGame()` still exist for
 * `dev/test-scenarios.js`'s one legitimate caller, a real teardown, not a player-facing path.
 */
// play-test: the board's fast fade-out on Quit, before the start screen renders
// (which fades itself in on return — game-start-entrance.js RETURN_FADE_IN_S).
// Smooths the board→start swap so it doesn't read as an abrupt one-frame cut.
const RETURN_FADE_OUT_S = 0.3;

export function showGameStart() {
  // The deliberate quit renders the start screen without a state fetch reaching
  // noteServerState (the fetch below resolves to the no-game state async, after the fade) — an
  // explicit sweep so an open release overlay / armed timer tears down immediately rather than
  // waiting on that round trip (multiplayer waiting-state UX design § The stage machine).
  resetWaitingState();
  // Stop in-flight animation chain and suppress "session lost" from its error recovery
  setIntentionalClear(true);
  finishPromptCommit();
  setAnimating(false);
  setHadApiError(false);
  // Close now rather than waiting for the eventual fetchState below to close it by phase — a
  // deliberate quit shouldn't leave the stream open (and its watermark stale) for the fade/clear
  // round-trip's duration.
  closePushStream();
  // Fade the board out and clear the local handle in parallel, then re-render to the start screen
  // once the fade has settled and the tail is free — fetchState() with no handle renders the
  // no-game state.
  clearCurrentGame();
  // The render WAITS for the dispatch tail rather than racing it, and RETRIES until it lands:
  // renderFetchedState skips a render that meets an occupied tail and skipping is never
  // deferring, so a quit fired during the round-start replay used to leave the board stranded at
  // the fade's `autoAlpha: 0` until a reload (quit-on-occupied-tail-strands-faded-board). The
  // loop's rule is the teardown's whole job: after every attempt, if anything still holds the
  // tail, wait and render again. Both re-claims that catches are real repairs, not defensiveness:
  //  - one landing in the microtask hops between whenDispatchTailIdle's last check and either
  //    render guard (this one, or boot.js#routeFetchRender's re-check past its own await) drops
  //    our render — the strand this whole slice exists to close, now re-fired rather than lost
  //    to hop counts across three modules that nobody can hold still;
  //  - one landing DURING the render means that burst's advanceCurrentState re-seats a phase-ful
  //    state under the freshly-rendered start screen (the failure game.js#render's own
  //    `setCurrentState` comment records), which rendering again once it drains repairs — at the
  //    cost of a second entrance build, visible where that first pass had already painted.
  // The read is a proxy, and its one blind spot is worth naming rather than implying a guarantee
  // it can't give: renderFetchedState returns its state whether or not routeFetchRender rendered,
  // so a claim that both appears AND clears within the few microtask hops from that inner guard
  // back to this condition would exit the loop unrendered. No production beat is that short —
  // every burst and replay beat settles over at least a frame (dispatch.js#drainBurst,
  // display-pause-animation.js#playDealThenReplay) — so that case is theoretical, unlike the two
  // above. It terminates because the quit already cleared the handle and closed the stream: no
  // new request can reach the network (a queued bid accept still calls submitBidPipelined, but
  // postGame throws without a handle and bid-pipeline.js resets on the throw), so only work
  // already in flight can claim, and every pass waits for the tail to drain rather than spinning
  // on it. Deferring a TEARDOWN is safe where deferring a resync was not — the state it renders
  // is "no game", which cannot go stale while it waits.
  fadeNodeOut(getApp(), RETURN_FADE_OUT_S).then(async () => {
    setIntentionalClear(false);
    do {
      await whenDispatchTailIdle();
      await fetchState();
    } while (isDispatchTailBusy());
  });
}
